import {
  processLinkedIn,
  processPitchDeck,
  processWebsite,
  createDocument as sdkCreateDocument,
  v2AcceptInvite,
  v2CreateInvestor,
  v2CreateStartup,
  v2LinkStartupDocument,
  v2LookupInvite,
  v2UpdateInvestorProfile,
  v2UpdateMe,
  verifyDocument,
} from '@workspace/api-backend'
import type { OnboardingApi } from '@workspace/seedforge-features/onboarding'
import { trackEvent } from './gtag'

/**
 * Extract `data` from a `{ status: 'ok', data: T } | { status: 'error', ... }` response.
 * Safe to use when `throwOnError: true` guarantees the success path.
 */
function extractData<T>(response: { data?: { status: string; data?: T } }): T {
  const body = response.data as { status: 'ok'; data: T }
  return body.data
}

export function createOnboardingApiFromSdk(): OnboardingApi {
  return {
    async setRole(role: 'founder' | 'investor') {
      await v2UpdateMe({
        body: { userType: role.toUpperCase() as 'FOUNDER' | 'INVESTOR' },
        throwOnError: true,
      })
      trackEvent('onboarding_started', { user_type: role })
    },

    async scrapeLinkedIn(url: string) {
      const res = await processLinkedIn({ body: { url }, throwOnError: true })
      return extractData(res)
    },

    async createDocument(fileName: string) {
      const res = await sdkCreateDocument({ body: { fileName }, throwOnError: true })
      return extractData(res) as {
        id: string
        fileName: string
        status: string
        uploadUrl: string
        uploadFields: Record<string, string>
      }
    },

    async uploadToPresignedUrl(
      uploadUrl: string,
      uploadFields: Record<string, string>,
      file: File
    ) {
      const formData = new FormData()
      for (const [key, value] of Object.entries(uploadFields)) {
        formData.append(key, value)
      }
      formData.append('file', file)

      const res = await fetch(uploadUrl, { method: 'POST', body: formData })
      if (!res.ok && res.status !== 204) {
        throw new Error(`Upload failed: ${res.status}`)
      }
    },

    async completeDocumentUpload(documentId: string) {
      await verifyDocument({ path: { id: documentId }, throwOnError: true })
    },

    async extractDeck(documentId: string) {
      const res = await processPitchDeck({ body: { documentId }, throwOnError: true })
      return extractData(res)
    },

    async scrapeWebsite(url: string, role: 'founder' | 'investor') {
      try {
        const res = await processWebsite({ body: { url, role }, throwOnError: true })
        return extractData(res)
      } catch {
        return null
      }
    },

    async createInvestor(investorData: { name: string; websiteUrl?: string }) {
      const res = await v2CreateInvestor({ body: investorData, throwOnError: true })
      return extractData(res) as { id: string }
    },

    async updateInvestor(id: string, investorData: Record<string, unknown>) {
      await v2UpdateInvestorProfile({
        path: { investorId: id },
        body: investorData as NonNullable<Parameters<typeof v2UpdateInvestorProfile>[0]['body']>,
        throwOnError: true,
      })
    },

    async updateUser(userData: Record<string, unknown>) {
      await v2UpdateMe({
        body: userData as NonNullable<Exclude<Parameters<typeof v2UpdateMe>[0], undefined>['body']>,
        throwOnError: true,
      })
    },

    async createStartup(startupData: Record<string, unknown>) {
      const res = await v2CreateStartup({
        body: startupData as Parameters<typeof v2CreateStartup>[0]['body'],
        throwOnError: true,
      })
      return extractData(res) as { id: string }
    },

    async linkDocument(
      startupId: string,
      documentId: string,
      options?: { label?: string; type?: string; investorId?: string }
    ) {
      await v2LinkStartupDocument({
        path: { startupId },
        body: { documentId, ...options } as Parameters<typeof v2LinkStartupDocument>[0]['body'],
        throwOnError: true,
      })
    },

    async completeOnboarding() {
      await v2UpdateMe({
        body: { onboardingCompleted: true },
        throwOnError: true,
      })
      trackEvent('onboarding_completed', {})
      return { onboardingCompleted: true }
    },

    async acceptInvite(token: string) {
      const res = await v2AcceptInvite({ body: { token }, throwOnError: true })
      return extractData(res) as {
        startupId: string | null
        startupName: string | null
        investorId: string | null
        investorName: string | null
        role: string
      }
    },

    async lookupInvite(token: string) {
      const res = await v2LookupInvite({ path: { token }, throwOnError: true })
      return extractData(res) as {
        email: string
        startupName: string | null
        investorName: string | null
        role: string
      }
    },
  }
}
